import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranscription, type TranscriptionState } from '@/hooks/useTranscription';
import { useDeepgramTranscription } from '@/hooks/useDeepgramTranscription';
import { getTranscribeConfig, setSessionSpeakerRole } from '@/lib/api-client';
import { getPreferredMicDeviceId, setPreferredMicDeviceId } from '@/lib/micDevice';
import type { SpeakerRole, SpeakerRoleMap, Turn } from '@/lib/types';

// One TranscriptionProvider per /hearing route. Both the ListeningButton in the
// Header and the TranscriptPanel in the body read and act on the same
// recognition session, so the engines are hoisted above Header here.
//
// Sprint P5 S1: chooses between Deepgram (server-proxied) and Web Speech, with an
// automatic fallback to Web Speech if Deepgram fails mid-hearing.
// Sprint P6 S1: surfaces diarized speaker turns + a per-session speaker→role map,
// with one-tap role tagging that persists immediately.

export type TranscriptionMode = 'deepgram' | 'webspeech';

export interface UnifiedTranscriptionState extends TranscriptionState {
  costUsd: number;
  audioSeconds: number;
  engine: TranscriptionMode;
  preferredMode: TranscriptionMode;
  setPreferredMode: (mode: TranscriptionMode) => void;
  deepgramAvailable: boolean;
  degraded: boolean;
  /** Diarized speaker turns (P6 S1); empty on Web Speech. */
  turns: Turn[];
  /** Per-session speaker index → role map. */
  speakerRoles: SpeakerRoleMap;
  /** Tag a speaker with a role (updates state + persists immediately). */
  setSpeakerRole: (speaker: number, role: SpeakerRole) => void;
  /** Courtroom-ready sprint: the Deepgram socket dropped and is being
   *  re-established (listening continues; quiet indicator shows). */
  reconnecting: boolean;
  /** Courtroom-ready sprint: listening, but no transcript text (final or
   *  interim) has arrived for 30s — quiet "check the mic" warning. */
  stalled: boolean;
  /** Mic-health sprint: available audio input devices (labels populate once
   *  mic permission has been granted). */
  micDevices: MediaDeviceInfo[];
  /** Mic-health sprint: the operator's selected input deviceId; '' = default. */
  micDeviceId: string;
  /** Mic-health sprint: select an input device (persists; restarts a live
   *  Deepgram capture so the change takes effect immediately). */
  setMicDevice: (deviceId: string) => void;
  /** Mic-health sprint: live input level 0..1 from the captured stream. */
  inputLevel: number;
  /** Mic-health sprint: captured audio has been silent for 10s while
   *  listening — the prominent "no audio detected" banner shows. */
  micSilent: boolean;
  /** Mic-health sprint: visible notice when the selected device vanished and
   *  capture fell back to the system default. */
  micNotice: string | null;
}

// Stall detection: how long without any new text while listening counts as a
// stall, and how often we check. Engine-agnostic — it watches whatever engine
// is live, so a silent Web Speech failure surfaces too.
const STALL_AFTER_MS = 30_000;
const STALL_CHECK_MS = 5_000;

const TranscriptionContext = createContext<UnifiedTranscriptionState | null>(null);

const MODE_STORAGE_KEY = 'cc_transcription_mode';

export function loadPreferredMode(): TranscriptionMode {
  try {
    const raw = window.localStorage.getItem(MODE_STORAGE_KEY);
    return raw === 'webspeech' ? 'webspeech' : 'deepgram';
  } catch {
    return 'deepgram';
  }
}

export function savePreferredMode(mode: TranscriptionMode): void {
  try {
    window.localStorage.setItem(MODE_STORAGE_KEY, mode);
  } catch {
    // localStorage may be unavailable; mode just won't persist.
  }
}

interface TranscriptionProviderProps {
  initial?: string;
  lang?: string;
  matterId?: string | null;
  sessionId?: string | null;
  initialTurns?: Turn[];
  initialSpeakerRoles?: SpeakerRoleMap;
  children: ReactNode;
}

export function TranscriptionProvider({
  initial,
  lang,
  matterId,
  sessionId,
  initialTurns,
  initialSpeakerRoles,
  children,
}: TranscriptionProviderProps) {
  const webSpeech = useTranscription({
    ...(initial !== undefined && { initial }),
    ...(lang !== undefined && { lang }),
  });

  const [deepgramAvailable, setDeepgramAvailable] = useState(false);
  const [preferredMode, setPreferredModeState] = useState<TranscriptionMode>(loadPreferredMode);
  const [degraded, setDegraded] = useState(false);
  const [speakerRoles, setSpeakerRoles] = useState<SpeakerRoleMap>(initialSpeakerRoles ?? {});

  useEffect(() => {
    let active = true;
    getTranscribeConfig()
      .then((cfg) => {
        if (active) {
          setDeepgramAvailable(cfg.deepgramAvailable);
        }
      })
      .catch(() => {
        // Config unreachable: stay on Web Speech.
      });
    return () => {
      active = false;
    };
  }, []);

  const deepgram = useDeepgramTranscription({
    ...(initial !== undefined && { initial }),
    ...(initialTurns !== undefined && { initialTurns }),
    matterId: matterId ?? null,
    onFallback: (carry) => {
      webSpeech.reset(carry);
      setDegraded(true);
      if (webSpeech.supported) {
        window.setTimeout(() => webSpeech.start(), 0);
      }
    },
  });

  const engine: TranscriptionMode =
    preferredMode === 'deepgram' && deepgramAvailable && !degraded && deepgram.supported
      ? 'deepgram'
      : 'webspeech';

  const activeEngine = engine === 'deepgram' ? deepgram : webSpeech;

  // Stall watch (courtroom-ready sprint): any change to the live text counts as
  // activity; 30s of listening with no activity raises the quiet warning. The
  // clock restarts whenever listening starts so a quiet courtroom moment right
  // after pressing Start doesn't false-alarm until a genuine 30s has passed.
  const [stalled, setStalled] = useState(false);
  const lastActivityRef = useRef(Date.now());
  const { transcript: liveTranscript, interim: liveInterim, isListening: liveListening } =
    activeEngine;
  useEffect(() => {
    lastActivityRef.current = Date.now();
    setStalled(false);
  }, [liveTranscript, liveInterim, liveListening]);
  useEffect(() => {
    if (!liveListening) {
      setStalled(false);
      return;
    }
    const timer = window.setInterval(() => {
      setStalled(Date.now() - lastActivityRef.current >= STALL_AFTER_MS);
    }, STALL_CHECK_MS);
    return () => window.clearInterval(timer);
  }, [liveListening]);

  const setPreferredMode = useCallback((mode: TranscriptionMode) => {
    savePreferredMode(mode);
    setPreferredModeState(mode);
    setDegraded(false);
  }, []);

  // Mic device picker (mic-health sprint): enumerate inputs, watch for devices
  // coming and going, and drop back to the system default — visibly — if the
  // saved device disappears. Labels only populate once mic permission has been
  // granted, so we re-enumerate when listening starts.
  const [micDevices, setMicDevices] = useState<MediaDeviceInfo[]>([]);
  const [micDeviceId, setMicDeviceIdState] = useState<string>(() => getPreferredMicDeviceId() ?? '');
  const [unplugNotice, setUnplugNotice] = useState<string | null>(null);
  const micDeviceIdRef = useRef(micDeviceId);
  micDeviceIdRef.current = micDeviceId;

  useEffect(() => {
    const media = navigator.mediaDevices;
    if (!media?.enumerateDevices) {
      return;
    }
    let active = true;
    const refresh = (): void => {
      media
        .enumerateDevices()
        .then((list) => {
          if (!active) {
            return;
          }
          const mics = list.filter((d) => d.kind === 'audioinput' && d.deviceId);
          setMicDevices(mics);
          // Only trust "device gone" once permission has revealed real ids.
          const selected = micDeviceIdRef.current;
          if (selected && mics.length > 0 && !mics.some((d) => d.deviceId === selected)) {
            setPreferredMicDeviceId(null);
            setMicDeviceIdState('');
            setUnplugNotice('Selected microphone is no longer connected — using the system default.');
          }
        })
        .catch(() => {
          // Enumeration failing just leaves the picker on "default".
        });
    };
    refresh();
    media.addEventListener?.('devicechange', refresh);
    return () => {
      active = false;
      media.removeEventListener?.('devicechange', refresh);
    };
  }, [liveListening]);

  const setMicDevice = useCallback(
    (deviceId: string) => {
      setPreferredMicDeviceId(deviceId || null);
      setMicDeviceIdState(deviceId);
      setUnplugNotice(null);
      // A live Deepgram capture recycles onto the new device immediately; the
      // lawful-use acknowledgement already given this session still stands.
      if (engine === 'deepgram' && deepgram.isListening) {
        deepgram.stop();
        window.setTimeout(() => deepgram.start(), 250);
      }
    },
    [engine, deepgram],
  );

  const reset = useCallback(
    (next = '') => {
      deepgram.reset(next);
      webSpeech.reset(next);
    },
    [deepgram, webSpeech],
  );

  const setSpeakerRole = useCallback(
    (speaker: number, role: SpeakerRole) => {
      setSpeakerRoles((prev) => ({ ...prev, [String(speaker)]: role }));
      if (sessionId) {
        setSessionSpeakerRole(sessionId, speaker, role).catch((err: unknown) => {
          console.warn('speaker role save failed:', err);
        });
      }
    },
    [sessionId],
  );

  const value: UnifiedTranscriptionState = {
    transcript: activeEngine.transcript,
    interim: activeEngine.interim,
    isListening: activeEngine.isListening,
    supported: activeEngine.supported,
    error: activeEngine.error,
    userStopped: activeEngine.userStopped,
    start: activeEngine.start,
    stop: activeEngine.stop,
    reset,
    costUsd: deepgram.costUsd,
    audioSeconds: deepgram.audioSeconds,
    engine,
    preferredMode,
    setPreferredMode,
    deepgramAvailable,
    degraded,
    turns: engine === 'deepgram' ? deepgram.turns : [],
    speakerRoles,
    setSpeakerRole,
    reconnecting: engine === 'deepgram' ? deepgram.reconnecting : false,
    stalled,
    micDevices,
    micDeviceId,
    setMicDevice,
    inputLevel: engine === 'deepgram' ? deepgram.inputLevel : 0,
    micSilent: engine === 'deepgram' ? deepgram.micSilent : false,
    micNotice: unplugNotice ?? (engine === 'deepgram' ? deepgram.deviceNotice : null),
  };

  return <TranscriptionContext.Provider value={value}>{children}</TranscriptionContext.Provider>;
}

export function useTranscriptionContext(): UnifiedTranscriptionState {
  const ctx = useContext(TranscriptionContext);
  if (!ctx) {
    throw new Error('useTranscriptionContext must be used inside <TranscriptionProvider>');
  }
  return ctx;
}
